// src/components/CommentDialog.js
import * as React from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography,
  Button, IconButton, CircularProgress, Link as MuiLink
} from '@mui/material';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { ThumbUp, ThumbDown, Send, Close, MoreHoriz } from '@mui/icons-material';
import { useLocation, Link } from 'react-router-dom';
import config from '../config';
import { getAuthToken, authenticatedRequest } from '../utils/api';

const server = config.server;

// 新增：处理时间格式化
function utc2local(utc_str) {
    if (!utc_str) return '';
    const utc = new Date(utc_str);
    let offset = utc.getTimezoneOffset();
    let local = new Date(utc.getTime() - offset * 60000);

    const year = local.getFullYear()
    const month = (local.getMonth() + 1).toString().padStart(2, '0');
    const day = local.getDate().toString().padStart(2, '0');
    const hours = local.getHours().toString().padStart(2, '0');
    const minutes = local.getMinutes().toString().padStart(2, '0');
    const seconds = local.getSeconds().toString().padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

const renderTextWithLinks = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <MuiLink
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ wordBreak: 'break-all', fontFamily: 'Microsoft JhengHei' }}
      >
        {part}
      </MuiLink>
    ) : (
      <span key={i} style={{ fontFamily: 'Microsoft JhengHei', whiteSpace: 'pre-wrap' }}>{part}</span>
    )
  );
};

const CommentDialog = ({ open, onClose, tweet, isAuthenticated }) => {
  const location = useLocation();
  const [comments, setComments] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [newCommentText, setNewCommentText] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const fetchComments = async (pageToFetch = 0) => {
    if (!tweet) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${server}/api/tweets/${tweet.tweet_id}/comments?page=${pageToFetch}&limit=10`);
      const data = await res.json();
      const sorted = Array.isArray(data) ? [...data].sort((a, b) => (b.likes || 0) - (a.likes || 0)) : [];
      if (pageToFetch === 0) setComments(sorted);
      else setComments(prev => [...prev, ...sorted]);
      if (!Array.isArray(data) || data.length < 10) setHasMore(false);
      setPage(pageToFetch);
    } catch (err) {
      console.error('無法獲取評論：', err);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (open) {
      setPage(0);
      setComments([]);
      setHasMore(true);
      fetchComments(0);
    }
  }, [open, tweet?.tweet_id]);

  const handleLoadMore = () => fetchComments(page + 1);

  const handlePostComment = async () => {
    if (!newCommentText.trim()) { alert('請輸入評論內容！'); return; }
    if (newCommentText.length > 1000) { alert('評論不得超過 1000 字！'); return; }

    const token = getAuthToken();
    if (!token) { alert('請登入後再發布評論！'); return; }

    try {
      const res = await authenticatedRequest(`/tweets/${tweet.tweet_id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ comment_text: newCommentText })
      });
      if (!res) return;
      if (res.ok) {
        const newComment = await res.json();
        setComments(prev => [newComment, ...prev].sort((a, b) => (b.likes || 0) - (a.likes || 0)));
        setNewCommentText('');
      } else {
        const errText = await res.text().catch(() => '');
        console.error('發布評論錯誤：', res.status, errText);
        alert(`發布評論失敗：${res.status}`);
      }
    } catch (err) {
      console.error('發佈評論失敗：', err);
      alert('網路錯誤，請稍後再試。');
    }
  };

  const handleVote = async (commentId, is_like_action) => {
    const token = getAuthToken();
    if (!token) { alert('請登入後再點讚/點踩！'); return; }
    try {
      const res = await authenticatedRequest(`/comments/${commentId}/vote`, {
        method: 'POST',
        body: JSON.stringify({ is_like: is_like_action })
      });
      if (!res) return;
      if (res.ok) {
        let resultJson = null;
        try { resultJson = await res.json(); } catch { resultJson = null; }
        if (resultJson && (typeof resultJson.likes === 'number' || typeof resultJson.dislikes === 'number')) {
          setComments(prev =>
            prev.map(c => c.id === commentId ? { ...c, likes: resultJson.likes, dislikes: resultJson.dislikes } : c)
                .sort((a, b) => (b.likes || 0) - (a.likes || 0))
          );
        } else {
          const msg = resultJson?.message || '';
          setComments(prev =>
            prev.map(c => {
              if (c.id !== commentId) return c;
              let likes = c.likes || 0;
              let dislikes = c.dislikes || 0;
              if (msg.includes('removed')) {
                if (is_like_action) likes = Math.max(0, likes - 1);
                else dislikes = Math.max(0, dislikes - 1);
              } else if (msg.includes('changed')) {
                if (is_like_action) { likes += 1; dislikes = Math.max(0, dislikes - 1); }
                else { dislikes += 1; likes = Math.max(0, likes - 1); }
              } else {
                if (is_like_action) likes += 1; else dislikes += 1;
              }
              return { ...c, likes, dislikes };
            }).sort((a, b) => (b.likes || 0) - (a.likes || 0))
          );
        }
      } else {
        const errText = await res.text().catch(() => '');
        console.error('投票失敗：', res.status, errText);
        alert('投票失敗，請稍後再試。');
      }
    } catch (err) {
      console.error('投票過程發生錯誤：', err);
      alert('投票失敗，請稍後再試。');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ m: 0, p: 2, fontFamily: 'Microsoft JhengHei' }}>
        評論（{tweet?.comment_count ?? comments.length}）
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ fontFamily: 'Microsoft JhengHei' }}>
        {tweet && (
          <Box sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 2, backgroundColor: '#fafafa' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontFamily: 'Microsoft JhengHei' }}>
              {tweet.agency}
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'Microsoft JhengHei', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {renderTextWithLinks(tweet.tweet_text)}
            </Typography>
          </Box>
        )}

        {isAuthenticated ? (
          <Box sx={{ mb: 2 }}>
            <TextareaAutosize
              minRows={2}
              maxRows={8}
              placeholder="留下你的評論（最多 1000 字）..."
              value={newCommentText}
              onChange={(e) => {
                const v = e.target.value;
                if (v.length <= 1000) setNewCommentText(v);
              }}
              style={{
                width: '100%',
                fontFamily: 'Microsoft JhengHei',
                resize: 'none',
                padding: '8px',
                borderRadius: 8,
                border: '1px solid #ccc',
                lineHeight: 1.5,
                boxSizing: 'border-box',
              }}
            />
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
              {newCommentText.length}/1000
            </Typography>
            <Button variant="contained" endIcon={<Send />} onClick={handlePostComment} sx={{ mt: 1, float: 'right' }}>
              發布
            </Button>
            <Box sx={{ clear: 'both' }} />
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 2 }}>
            請 <Link to="/signin" state={{ from: location }} onClick={onClose}>登入</Link> 後再評論。
          </Typography>
        )}

        {comments.length > 0 ? (
          comments.map(comment => (
            <Box key={comment.id} sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="primary.main">
                  {comment.user?.username || '匿名用戶'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {utc2local(comment.created_at)}
                </Typography>
              </Box>

              <Typography 
              variant="body1" 
              sx={{
                my: 1,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'Noto Sans CJK, Microsoft JhengHei, Arial, sans-serif',
                fontSize: '1rem',   // 直接設置字體大小
              }}>
                {comment.comment_text}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={() => handleVote(comment.id, true)} aria-label="like">
                  <ThumbUp fontSize="small" />
                </IconButton>
                <Typography variant="caption">{comment.likes ?? 0}</Typography>

                <IconButton size="small" onClick={() => handleVote(comment.id, false)} aria-label="dislike">
                  <ThumbDown fontSize="small" />
                </IconButton>
                <Typography variant="caption">{comment.dislikes ?? 0}</Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" align="center" color="text.secondary">暫無評論。</Typography>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {hasMore && !isLoading && comments.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <IconButton onClick={handleLoadMore} aria-label="查看更多">
              <MoreHoriz />
            </IconButton>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CommentDialog;
